import React, { useState } from 'react';
import { SeriesOptionsType } from 'highcharts';
import { Space } from 'antd';
import ExportSheet from '../exportSheet/exportSheet';
import { ChartProps, ChartSettingsProps } from '../../@types/data/types';
import { useTheme } from '../../providers/theme';
import ChartSettings from './settings';
import { getColors } from '../utils/getColors';
import { Chart } from './BaseChart';
import { ChartSpec } from './chartSpec';

const DashboardChart: React.FC<ChartProps> = ({
  liaoChartData,
  lambdaChartData,
  showMultipleData,
  receivedLiao,
  receivedLambda,
  name,
  xAxis,
  yAxis,
  equation,
  rSquared,
  log,
  sampleCode,
  height,
  containerWidth
}) => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const chartData = liaoChartData;
  const received = receivedLiao;
  const chartDataX = chartData?.map(point => point.x);
  const chartDataY = chartData?.map(point => point.y);
  const minY =
    chartDataY && chartDataY.length > 0 ? Math.min(...chartDataY) : undefined;
  const maxY =
    chartDataY && chartDataY.length > 0 ? Math.max(...chartDataY) : undefined;
  const minX =
    chartDataX && chartDataX.length > 0 ? Math.min(...chartDataX) : undefined;
  const maxX =
    chartDataX && chartDataX.length > 0 ? Math.max(...chartDataX) : undefined;
  const [chartSettings, setChartSettings] = useState<ChartSettingsProps>({
    xmin: 0,
    xmax: maxX,
    ymin: minY,
    ymax: maxY
  });

  const chartDefaults = ({
    xmin: 0,
    xmax: maxX,
    ymin: minY,
    ymax: maxY
  });

  const series: SeriesOptionsType[] = [];

  series.push({
    name: 'Data SCAL',
    type: 'scatter',
    data: chartData,
    color: '#f47936',
    marker: {
      radius: 5
    },
    zIndex: 2
  });

  series.push({
    name: 'Pc Liao',
    type: 'spline',
    data: received,
    color: '#72E2D8',
    marker: {
      enabled: false
    },
    lineWidth: 2,
    dataLabels: {
      style: {
        color: '#72E2D8'
      }
    },
    zIndex: 1
  });

  if (showMultipleData) {
    series.push({
      name: 'Pc Lambda',
      type: 'spline',
      data: receivedLambda,
      color: '#F5C242',
      marker: {
        enabled: false
      },
      lineWidth: 2,
      dataLabels: {
        style: {
          color: '#4A90E2'
        }
      }
    });
  }

  const spec: ChartSpec = {
    title: name,
    height,
    xAxis: {
      title: xAxis,
      type: log ? 'logarithmic' : 'linear',
      max: log ? 1 : chartSettings.xmax,
      min: log ? 0.001 : chartSettings.xmin
    },
    yAxis: {
      title: yAxis,
      type: log ? 'logarithmic' : 'linear',
      max: log ? 1000 : chartSettings.ymax,
      min: log ? 0.1 : chartSettings.ymin
    },
    series
  };

  const sheetData = chartData?.map(point => {
    return {
      x: point.x,
      y: point.y,
      sampleCode: sampleCode ? sampleCode : undefined
    };
  });

  return (
    <Space
      direction="vertical"
      style={{
        backgroundColor: 'transparent',
        marginTop: '1rem',
        border: `1px solid ${colors.border}`,
        borderRadius: '16px',
        width: containerWidth,
        transition: 'width 0.5s'
      }}
    >
      <Space style={{ display: 'block' }}>
        {equation !== undefined ? <p>{equation}</p> : null}
        {rSquared !== undefined ? <p> r²: {rSquared}</p> : null}
        <div style={{ borderBottom: `1px solid ${colors.border}` }}>
          <div
            style={{
              marginLeft: '3%',
              display: 'flex',
              padding: '0.75rem',
              justifyContent: 'space-between'
            }}
          >
            <h5>{sampleCode}</h5>
            <div style={{ display: 'flex' }}>
              <div style={{ marginRight: '0.5rem' }}>
                <ChartSettings
                  chartSettings={chartSettings}
                  setChartSettings={setChartSettings}
                  autoDefaults={chartDefaults}
                />
              </div>
              <div>
                <ExportSheet
                  chartData={sheetData}
                  liaoData={received}
                  fileName={name ? name : 'chart'}
                  xAxis={xAxis}
                  yAxis={yAxis}
                />
              </div>
            </div>
          </div>
        </div>
        <div style={{ padding: '0.75rem' }}>
          <Chart spec={spec} />
        </div>
      </Space>
    </Space>
  );
};

export default DashboardChart;
